<template>
  <b-modal
    :id="`modal-edit-created-at${id}`"
    centered
    size="md"
    title="修改建立時間"
    hide-footer
    @hide="resetModal"
  >
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="建立時間"
                rules="required"
              >
                <b-form-group
                  label="建立時間"
                  label-for="created_at"
                  class="required"
                >
                  <flat-pickr
                    v-model="invoiceData.created_at"
                    class="form-control"
                    :config="{
                      enableTime: true,
                      dateFormat: 'Y-m-d H:i',
                      time_24hr: true,
                      defaultDate: invoiceData.created_at,
                      locale: 'zh',
                    }"
                    placeholder="YYYY-MM-DD HH:mm"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" class="text-center mt-2">
              <b-button variant="primary" class="mr-1" type="submit">
                儲存
              </b-button>
              <b-button variant="outline-secondary" @click="hide">
                取消
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BButton,
  BOverlay,
  BRow,
  BCol,
  BFormInvalidFeedback,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/l10n/zh.js";

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BButton,
    BOverlay,
    BRow,
    BCol,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    flatPickr,
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    invoiceId: {
      type: Number,
      required: false,
      default: null,
    },
    currentCreatedAt: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      invoiceData: {
        id: this.invoiceId,
        created_at: this.currentCreatedAt
          ? new Date(this.currentCreatedAt).toISOString().slice(0, 16)
          : "",
      },
    };
  },
  watch: {
    currentCreatedAt: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.invoiceData.created_at = new Date(newVal)
            .toISOString()
            .slice(0, 16);
        }
      },
    },
    invoiceId: {
      immediate: true,
      handler(newVal) {
        this.invoiceData.id = newVal;
      },
    },
  },
  methods: {
    ...formValidation,
    hide() {
      this.$bvModal.hide(`modal-edit-created-at${this.id}`);
    },
    resetModal() {
      this.$nextTick(() => {
        this.invoiceData = {
          id: this.invoiceId,
          created_at: this.currentCreatedAt
            ? new Date(this.currentCreatedAt).toISOString().slice(0, 16)
            : "",
        };
      });
    },
    onSubmit() {
      this.$swal({
        title: "確認修改建立時間?",
        showCancelButton: true,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.loading = true;

          try {
            console.log("當前時間值：", this.invoiceData.created_at);

            // 檢查是否有有效的時間值
            if (!this.invoiceData?.created_at) {
              throw new Error("無效的時間值");
            }

            let dateStr = this.invoiceData.created_at;

            // 如果時間是 Date 對象，轉換為字符串
            if (dateStr instanceof Date) {
              dateStr = dateStr.toISOString();
            }

            // 處理空格分隔的時間格式
            dateStr = dateStr.replace(" ", "T");

            // 解析時間
            const [date, time] = dateStr.split("T");
            const [year, month, day] = date.split("-");
            const [hour, minute] = time.split(":");

            // 手動構建新的時間（加8小時）
            const newHour = (parseInt(hour) + 8) % 24;
            // 如果加8小時後超過24小時，需要增加一天
            const addDay = parseInt(hour) + 8 >= 24 ? 1 : 0;

            // 手動構建時間字符串
            const utcDateTime = `${year}-${month.padStart(2, "0")}-${String(
              parseInt(day) + addDay
            ).padStart(2, "0")}T${String(newHour).padStart(2, "0")}:${minute}`;

            store
              .dispatch("contract/updateCreatedAt", {
                id: this.invoiceData.id,
                created_at: utcDateTime,
              })
              .then((response) => {
                this.$swal({
                  text: response.data.message,
                  icon: "success",
                  customClass: {
                    confirmButton: "btn btn-primary",
                  },
                  buttonsStyling: false,
                });
                this.loading = false;
                this.hide();
                this.$emit("refetch-data");
              })
              .catch((error) => {
                this.loading = false;
                this.$swal({
                  text: error.response?.data?.message || "保存失敗",
                  icon: "error",
                  customClass: {
                    confirmButton: "btn btn-primary",
                  },
                  buttonsStyling: false,
                });
              });
          } catch (error) {
            console.error("時間處理錯誤：", error);
            this.loading = false;
            this.$swal({
              text: "時間格式錯誤，請重新選擇",
              icon: "error",
              customClass: {
                confirmButton: "btn btn-primary",
              },
              buttonsStyling: false,
            });
          }
        }
      });
    },
  },
};
</script>
